"use client";

import { type IOrderDeliveryAddressComponents, type ILocation } from '@order-up/models';
import { X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDeliveryAddressCoverage } from '../../../hooks/useDeliveryAddressCoverage';
import DeliveryAddressForm, {
    formatDeliveryAddress,
    isDeliveryAddressComplete,
} from './DeliveryAddressForm';

/**
 * DeliveryAddressDialog — the single, focused editor for a delivery address.
 *
 * One dialog, reused wherever a delivery address is captured (the cart opens it
 * instead of cluttering itself with an inline form). It runs the SAME coverage
 * preflight as the order-config popup, so the address is validated identically
 * everywhere — fee / ETA on success, "outside delivery area" on failure, and
 * Save is blocked for an unservable address.
 */
interface DeliveryAddressDialogProps {
    isOpen: boolean;
    onClose: () => void;
    restaurant?: ILocation | null;
    initialAddress?: IOrderDeliveryAddressComponents;
    onSave: (components: IOrderDeliveryAddressComponents, formatted: string) => void;
}

export default function DeliveryAddressDialog({
    isOpen,
    onClose,
    restaurant: locationRecord,
    initialAddress,
    onSave,
}: DeliveryAddressDialogProps) {
    const [addr, setAddr] = useState<IOrderDeliveryAddressComponents>(initialAddress ?? {});
    const wasOpenRef = useRef(false);

    // Seed with the saved address ONLY on the closed→open transition. Seeding on
    // every `initialAddress` identity change (it's cart-derived) would wipe the
    // address the user is mid-edit whenever the cart re-emits.
    useEffect(() => {
        if (isOpen && !wasOpenRef.current) setAddr(initialAddress ?? {});
        wasOpenRef.current = isOpen;
    }, [isOpen, initialAddress]);

    useEffect(() => {
        if (!isOpen) return;
        const onEsc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
        document.addEventListener('keydown', onEsc);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onEsc);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);

    const coverage = useDeliveryAddressCoverage({
        restaurantId: locationRecord?.restaurantId,
        tenantId: locationRecord?.tenantId,
        isDelivery: isOpen,
        deliveryAddressComponents: addr,
    });

    const complete = isDeliveryAddressComplete(addr);
    const canSave =
        complete && coverage.status !== 'not_deliverable' && coverage.status !== 'checking';

    if (!isOpen) return null;

    const content = (
        <div
            className="fixed inset-0 z-[70] flex items-end md:items-center justify-center bg-black/50"
            onClick={onClose}
        >
            <div
                className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-2xl shadow-2xl max-h-[90dvh] overflow-y-auto pb-[env(safe-area-inset-bottom,0px)] md:pb-0"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between p-5 pb-3">
                    <h2 className="text-lg font-bold text-gray-900">Delivery address</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors pointer-coarse:p-3"
                        aria-label="Close delivery address"
                    >
                        <X className="h-5 w-5 text-gray-500" aria-hidden />
                    </button>
                </div>

                <div className="px-5 pb-5 space-y-3">
                    <DeliveryAddressForm value={addr} onChange={setAddr} />

                    {complete && coverage.status === 'checking' && (
                        <p className="text-xs text-gray-500">Checking delivery availability…</p>
                    )}
                    {complete && coverage.status === 'deliverable' && (
                        <p className="text-xs text-green-700 font-medium">
                            We deliver here
                            {coverage.feeCents !== undefined
                                ? ` — $${(coverage.feeCents / 100).toFixed(2)} delivery fee`
                                : ''}
                            {coverage.etaMinutes !== undefined ? ` · ~${coverage.etaMinutes} min` : ''}
                        </p>
                    )}
                    {complete && coverage.status === 'not_deliverable' && (
                        <p className="text-xs text-[var(--menu-primary,#dc2626)] font-medium">
                            {coverage.message ?? 'This address is outside our delivery area.'}
                        </p>
                    )}

                    <button
                        type="button"
                        disabled={!canSave}
                        onClick={() => onSave(addr, formatDeliveryAddress(addr))}
                        className="w-full py-3 rounded-xl text-sm font-semibold bg-[var(--menu-primary,#dc2626)] text-white hover:bg-[var(--menu-primary-hover,#b92020)] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                    >
                        {complete && coverage.status === 'checking' ? 'Checking…' : 'Save address'}
                    </button>
                </div>
            </div>
        </div>
    );

    return typeof window !== 'undefined' ? createPortal(content, document.body) : null;
}
